import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDateTime } from "src/utils/dateTime";
import { CursorExportPopup } from "../../modules/cursorExport/CursorExportPopup";
import type { CursorExportJob } from "../../modules/cursorExport/cursorExport";
import { sessionExportRequestData, parseSessionExportPage } from "../../services/sessionCsvExport";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { DatePickerField, DateRangePicker } from "src/view/conponents/DateRangePicker";
import Input, { InputBoxStyle, InputFontStyle } from "@ui/input/Input";
import { Select } from "@ui/select/Select";
import Spacer from "@ui/spacer/Spacer";
import { Typo, TypoCaption } from "@ui/text/Typo";
import { Archive, Banknote, BarChart3, ChevronDown, ChevronUp, CheckCircle2, Clock, Download, Filter as FilterIcon, Lock, Percent, TrendingUp, Undo2, Wallet, X, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TFeaturedComponent } from "src/Featuresmap";
import { GD } from "src/GD";
import { IPaymentSession } from "src/types/types";
import { CountryFlag } from "../conponents/icons/CountryFlag";
import { IPagination, Pagination } from "../conponents/Pagination";
import { TFormType } from "./BaseForm";
import { formatAmount, savePdf, saveSummaryPdf } from "src/tools/tools";
import type { SummaryPDFRow } from "src/tools/pdf/SummaryPDF";
import { forceSessionWebhook } from "../../tools/sessionActions";
import { S_CHANGE_SESSION_STATUS_POPUP } from "../conponents/popup/ChangeSessionStatusPopup";
import { S_REFUND_POPUP } from "../conponents/popup/RefundPopup";
import { Table } from "@ui/table/Table";
import { notify } from "../../tools/notify";
import { IconVisa } from "@ui/icons/IconVisa";
import { IconMastercard } from "@ui/icons/IconMastercard";
import { HoverTooltip } from "../conponents/HoverTooltip";
import { Error } from "@ui/error/Error";
import { hasRole } from "src/permissions";
import { portfolioFilterSurface } from "../conponents/list/portfolioFilterStyle";
import { IconApple } from "src/assets/terminal/icon_apple";
import { IconBank } from "src/assets/terminal/icon_bank";
import { IconCard } from "src/assets/terminal/icon_card";
import { IconGoogle } from "src/assets/terminal/icon_google";
import { CountriesData } from "@ui/utils/CountriesData";
import { NoRecords } from "../providers/NoRecords";
import { BATCH_EXPORT_USER_IDS } from "src/services/transactionBatchExport";
import { TransactionBatchExportPopup, type TransactionBatchSnapshot } from "../popup/TransactionBatchExportPopup";

type TSessionProps = {
	target_id: any;
	target_type: TFormType;
	filterable?: boolean;
	filterAppearance?: "default" | "portfolio";
	types?: string[];
	tableScrollable?: boolean;
	hideMerchantColumn?: boolean;
	/** Applied to every request, including pagination, refresh and export. */
	fixedFilters?: { from?: string; to?: string; currency?: string; search?: string };
	requestOptions?: Pick<IAPIRequest, "expectedProjectId" | "signal">;
	showSummary?: boolean;
	/** A containing dialog may open its own preview without replacing the global one. */
	onOpenSession?: (session: IPaymentSession) => void;
	/** Ground the table, the pinned filter bundle and the footer are painted with; matches the surrounding area. */
	surface?: string;
};

interface IMerchantFilterItem {
	id: number;
	client_id?: number;
	name?: string | null;
	domain?: string | null;
	status?: string;
}

const perPage = 20;

const collapsibleSessionColumns = ["fail_reason", "reserved", "fee", "email", "instrument", "mid"] as const;

// localStorage keys for persisting the user's filter/summary collapse preferences.
// Global, not target-scoped — collapse pref carries across pages/clients/merchants.
const LS_FILTER_COLLAPSED_KEY = "sessions.filterCollapsed";
const LS_SUMMARY_COLLAPSED_KEY = "sessions.summaryCollapsed";
type TCollapsibleSessionColumn = (typeof collapsibleSessionColumns)[number];

// IMPORTANT: these are not max widths — they are *estimates* used by the column-collapse
// algorithm to predict whether the table fits the viewport. Overestimating causes columns to
// be hidden when there is actually room. Aim for typical-content width including padding,
// not worst-case width. Long content can ellipsize / wrap, which is preferable to spurious
// hiding while space is clearly available.
const sessionColumnWidthFallback: Record<string, number> = {
	id: 170,
	status: 170,
	type: 140,
	mid: 110,
	email: 200,
	instrument: 130,
	reserved: 86,
	fee: 86,
	amount: 120,
	fail_reason: 260,  // bonus column — matches its CSS max (`minmax(140px, 260px)`). Stays
	                   // first-in-line for collapse, but only because of its position in the
	                   // `collapsibleSessionColumns` array, not because of an inflated estimate.
	actions: 50,
};
const TABLE_OVERFLOW_TOLERANCE = 12;
const NO_CLIENT_MERCHANTS_ERROR_CODE = 17;

const isNoClientMerchantsError = (error?: IError) => {
	if (!error) return false;
	return error.code === NO_CLIENT_MERCHANTS_ERROR_CODE && typeof error.details === "string" && error.details.includes("no merchants found for client");
};

const sessionCsvHeaderPriority = [
	"id",
	"created_at",
	"updated_at",
	"status",
	"type",
	"amount",
	"currency",
	"country",
	"mid",
	"email",
	"instrument",
	"instrument_type",
	"instrument_name",
	"institution",
	"settlement_status",
	"statement_id",
	"statement_status",
	"fail_reason",
	"payment_id",
	"processing_id",
	"terminal_id",
	"channel_id",
	"project_id",
	"webhook_status",
	"webhook_at",
	"sandbox",
];

const getSessionCsvHeaders = (rows: Record<string, unknown>[]) => {
	const availableHeaders = Array.from(new Set(rows.flatMap(row => Object.keys(row))))
		.filter(header => header !== "processing_mid" && header !== "current_channel_index" && header !== "statement_info");
	const prioritizedHeaders = sessionCsvHeaderPriority.filter(header => availableHeaders.includes(header));
	const extraHeaders = availableHeaders.filter(header => !sessionCsvHeaderPriority.includes(header)).sort();

	return [...prioritizedHeaders, ...extraHeaders];
};

const getSessionCsvCellValue = (header: string, value: unknown) => {
	if (header === "amount" && typeof value === "number") {
		return (value / 100).toFixed(2);
	}

	if (value == null) {
		return "";
	}

	if (typeof value === "object") {
		return JSON.stringify(value);
	}

	return String(value);
};

const escapeCsvCell = (value: string) => {
	return value.includes(",") || value.includes('"') || value.includes("\n") ? '"' + value.replace(/"/g, '""') + '"' : value;
};

const sanitizeFileNamePart = (value: string) => value.replace(/[^a-zA-Z0-9_-]/g, "_");

const DEFAULT_FROM_DATE_DAYS = 30;
const END_OF_DAY_HOUR = 23;
const END_OF_DAY_MINUTE = 59;
const END_OF_DAY_SECOND = 59;
const END_OF_DAY_MILLISECOND = 999;

const defaultFromDate = () => {
	const date = new Date();
	date.setUTCDate(date.getUTCDate() - DEFAULT_FROM_DATE_DAYS);
	date.setUTCHours(0, 0, 0, 0);
	return date;
};

const defaultToDate = () => {
	const date = new Date();
	date.setUTCHours(END_OF_DAY_HOUR, END_OF_DAY_MINUTE, END_OF_DAY_SECOND, END_OF_DAY_MILLISECOND);
	return date;
};

const getValidDateOrFallback = (value: Date | string, fallback: () => Date): Date => {
	const date = value instanceof Date ? value : new Date(value);
	return Number.isNaN(date.getTime()) ? fallback() : date;
};

const formatDateParam = (date: Date): string => {
	// The API expands date-only boundaries to full UTC days, inclusively.
	const y = date.getUTCFullYear();
	const m = String(date.getUTCMonth() + 1).padStart(2, "0");
	const d = String(date.getUTCDate()).padStart(2, "0");
	return `${y}-${m}-${d}`;
};

interface IFilterOption {
	name: string;
	value: string;
}

const statusOptions: IFilterOption[] = [
	{ get name() { return msg("all"); }, value: "" },
	{ get name() { return msg("created"); }, value: "created" },
	{ get name() { return msg("pending"); }, value: "pending" },
	{ get name() { return msg("completed"); }, value: "completed" },
	{ get name() { return msg("failed_031a8f0f"); }, value: "failed" },
	{ get name() { return msg("expired"); }, value: "expired" },
	{ get name() { return msg("canceled"); }, value: "canceled" },
];

const settlementStatusOptions: IFilterOption[] = [
	{ get name() { return msg("all"); }, value: "" },
	{ get name() { return msg("completed"); }, value: "completed" },
	{ get name() { return msg("unknown"); }, value: "unknown" },
	{ get name() { return msg("pending"); }, value: "pending" },
	{ get name() { return msg("failed_031a8f0f"); }, value: "failed" },
];

const typeOptions: IFilterOption[] = [
	{ get name() { return msg("all"); }, value: "" },
	{ get name() { return msg("oct"); }, value: "oct" },
	{ get name() { return msg("transfer"); }, value: "transfer" },
	{ get name() { return msg("refund"); }, value: "refund" },
	{ get name() { return msg("chargeback"); }, value: "chargeback" },
	{ get name() { return msg("adjustmentIn"); }, value: "adjustment_in" },
	{ get name() { return msg("adjustmentOut"); }, value: "adjustment_out" },
];

const instrumentOptions: IFilterOption[] = [
	{ get name() { return msg("all"); }, value: "" },
	{ get name() { return msg("card"); }, value: "card" },
	{ get name() { return msg("applePay"); }, value: "ap" },
	{ get name() { return msg("googlePay"); }, value: "gp" },
	{ get name() { return msg("openBanking"); }, value: "ob" },
];

const getFilterOptionByValue = (options: IFilterOption[], value: string): IFilterOption | undefined => {
	return options.find(option => option.value === value);
};

const hasDisplayValue = (value: string | number | null | undefined) => value !== null && value !== undefined && String(value).trim() !== "";

const normalizeSessionItems = (data: any): IPaymentSession[] => {
	if (Array.isArray(data?.items)) return data.items;
	if (Array.isArray(data?.rows)) return data.rows;
	if (Array.isArray(data)) return data;
	return [];
};

// Summary block returned by /session/list when the period is narrow enough AND no
// filter has narrowed the result set. Null = the API skipped aggregation; the panels
// above the table should hide entirely in that case.
type TSessionsSummary = {
	turnover: number;          // sum of `amount` (cents) — all status=completed in created_at window
	turnover_count: number;
	settled_later: number;     // sum of `amount` (cents) — created in window, settlement_date after `to`
	settled_later_count: number;
	authorized: number;        // sum of `amount` (cents) — status=completed + no settlement_date
	authorized_count: number;
	settled: number;           // sum of `amount` (cents) — status=completed + settlement_date in window
	settled_count: number;
	failed: number;            // sum of `amount` (cents) — status IN ('failed','error')
	failed_count: number;
	refunded: number;          // sum of `amount` (cents) — refund_status='completed'
	refunded_count: number;
	rolling_reserved: number;  // sum of `rolling_reserve_amount` (already in EUR, decimal)
	fees: number;              // sum of `fees_amount` (already in EUR, decimal) — includes decline_fees
	net: number;               // EUR — settled (converted from cents) minus fees and rolling reserve
};

const normalizeSessionsSummary = (data: any): TSessionsSummary | null => {
	const s = data?.summary;
	if (!s || typeof s !== "object") return null;
	return {
		turnover: Number(s.turnover ?? 0),
		turnover_count: Number(s.turnover_count ?? 0),
		settled_later: Number(s.settled_later ?? 0),
		settled_later_count: Number(s.settled_later_count ?? 0),
		authorized: Number(s.authorized ?? 0),
		authorized_count: Number(s.authorized_count ?? 0),
		settled: Number(s.settled ?? 0),
		settled_count: Number(s.settled_count ?? 0),
		failed: Number(s.failed ?? 0),
		failed_count: Number(s.failed_count ?? 0),
		refunded: Number(s.refunded ?? 0),
		refunded_count: Number(s.refunded_count ?? 0),
		rolling_reserved: Number(s.rolling_reserved ?? 0),
		fees: Number(s.fees ?? 0),
		net: Number(s.net ?? 0),
	};
};

const formatSummaryCents = (cents: number): string =>
	(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatSummaryEur = (eur: number): string =>
	eur.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Build the PDF row set in the same order/format as the on-screen cards. "Settles later"
// is conditional (only when there's a deferred-settlement tail), Net is the highlighted
// bottom line. Cents-scoped metrics use the /100 formatter; rolling/fees/net are EUR decimals.
const buildSummaryPdfRows = (summary: TSessionsSummary): SummaryPDFRow[] => {
	const rows: SummaryPDFRow[] = [
		{ label: msg("turnover_8ecaf1d7"), amount: formatSummaryCents(summary.turnover), count: summary.turnover_count },
		{ label: msg("authorized"), amount: formatSummaryCents(summary.authorized), count: summary.authorized_count },
		{ label: msg("settled"), amount: formatSummaryCents(summary.settled), count: summary.settled_count },
	];
	if (summary.settled_later_count > 0)
		rows.push({ label: msg("settlesLater"), amount: formatSummaryCents(summary.settled_later), count: summary.settled_later_count });
	rows.push(
		{ label: msg("failed_031a8f0f"), amount: formatSummaryCents(summary.failed), count: summary.failed_count },
		{ label: msg("refunded"), amount: formatSummaryCents(summary.refunded), count: summary.refunded_count },
		{ label: msg("rollingReserved"), amount: formatSummaryEur(summary.rolling_reserved) },
		{ label: msg("fees_6d80486c"), amount: formatSummaryEur(summary.fees) },
		{ label: msg("net"), amount: formatSummaryEur(summary.net), highlight: true },
	);
	return rows;
};

// Accent palette + corresponding lucide icon for each summary metric. Colors used as
// accent for the icon-square only; the rest of the item stays neutral (light style).
type TSummaryEntry = {
	icon: typeof CheckCircle2;
	accent: string;
	bg: string;
};

const SUMMARY_ENTRIES: Record<string, TSummaryEntry> = {
	turnover:   { icon: TrendingUp,   accent: "#0f766e", bg: "#f0fdfa" },  // teal
	authorized: { icon: CheckCircle2, accent: "#15803d", bg: "#ecfdf5" },  // green
	settled:    { icon: Banknote,     accent: "#1e40af", bg: "#eff6ff" },  // blue
	settled_later: { icon: Clock,     accent: "#7c3aed", bg: "#f5f3ff" },  // violet
	failed:     { icon: XCircle,      accent: "#b91c1c", bg: "#fef2f2" },  // red
	refunded:   { icon: Undo2,        accent: "#334155", bg: "#f1f5f9" },  // slate
	rolling:    { icon: Lock,         accent: "#9a3412", bg: "#fff7ed" },  // orange
	fees:       { icon: Percent,      accent: "#854d0e", bg: "#fefce8" },  // amber
	net:        { icon: Wallet,       accent: "#0f766e", bg: "#f0fdfa" },  // teal (merchant payout)
};

// Small chip-button to expand a collapsed filter / summary panel back to its full form.
// Visual matches the filter panel's white-card style so the collapsed state still reads
// as "this panel is here, just compact".
const ExpandPill = ({
	label,
	icon,
	onClick,
}: {
	label: string;
	icon?: React.ReactNode;
	onClick: () => void;
}) => { useLocale(); return ((
	<Box
		onClick={onClick}
		title={msg("templateExpand_614e2df2", { v0: label.toLowerCase() })}
		sx={{
			display: "inline-flex",
			alignItems: "center",
			gap: "6px",
			padding: "0 12px",
			height: "36px",
			borderRadius: "6px",
			border: "1px solid #cbd5e1",
			backgroundColor: "#fff",
			cursor: "pointer",
			color: "#475569",
			fontSize: "13px",
			fontWeight: 500,
			whiteSpace: "nowrap",
			flexShrink: 0,
			transition: "background-color 0.15s, color 0.15s, border-color 0.15s",
			"&:hover": { backgroundColor: "#f8fafc", color: "#1e293b", borderColor: "#94a3b8" },
		}}>
		{icon}
		{label}
		<ChevronDown size={14} />
	</Box>
)); };

const SummaryItem = ({
	label,
	amount,
	count,
	currencyPrefix,
	entry,
}: {
	label: string;
	amount: string;
	count?: number;
	currencyPrefix?: string;
	entry: TSummaryEntry;
}) => {
	useLocale();
	const IconComponent = entry.icon;
	return (
		<HBox gap="10px" sx={{ minWidth: 0, alignItems: "center" }}>
			<Box
				sx={{
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					width: "32px",
					height: "32px",
					borderRadius: "8px",
					backgroundColor: entry.bg,
					color: entry.accent,
					flexShrink: 0,
				}}>
				<IconComponent size={16} strokeWidth={2.25} />
			</Box>
			<VBox sx={{ gap: "1px", minWidth: 0 }}>
				<Typo
					sx={{
						fontSize: "10px",
						color: "#64748b",
						textTransform: "uppercase",
						letterSpacing: "0.06em",
						fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
						fontWeight: 500,
						whiteSpace: "nowrap",
					}}>
					{label}
				</Typo>
				<HBox gap="5px" sx={{ alignItems: "baseline", minWidth: 0 }}>
					<Typo
						sx={{
							fontSize: "14px",
							fontWeight: 600,
							color: "#1e293b",
							fontVariantNumeric: "tabular-nums",
							whiteSpace: "nowrap",
						}}>
						{currencyPrefix && <span style={{ color: "#94a3b8", fontWeight: 400, marginRight: "2px" }}>{currencyPrefix}</span>}
						{amount}
					</Typo>
					{count !== undefined && (
						<Typo
							sx={{
								fontSize: "11px",
								color: "#94a3b8",
								fontVariantNumeric: "tabular-nums",
								whiteSpace: "nowrap",
							}}>
							· {count.toLocaleString()}
						</Typo>
					)}
				</HBox>
			</VBox>
		</HBox>
	);
};

const SessionsSummaryPanels = ({
	summary,
	from,
	to,
	onDismissFallback,
	onCollapse,
}: {
	summary: TSessionsSummary | null;
	from?: Date;
	to?: Date;
	onDismissFallback?: () => void;
	onCollapse?: () => void;
}) => { useLocale(); return ((
	<VBox
		sx={{
			background: "#fff",
			borderRadius: "14px",
			boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 2px 12px rgba(0,0,0,0.03)",
			padding: "14px 20px",
			gap: "10px",
		}}>
		<HBox sx={{ justifyContent: "space-between", alignItems: "center", minHeight: "16px" }}>
			<HBox gap="8px" sx={{ alignItems: "baseline", minWidth: 0 }}>
				<Typo
					sx={{
						fontSize: "10px",
						color: "#94a3b8",
						textTransform: "uppercase",
						letterSpacing: "0.08em",
						fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
						fontWeight: 600,
						whiteSpace: "nowrap",
					}}>{msg("summary")}</Typo>
				{from && to && (
					<Typo
						sx={{
							fontSize: "10px",
							color: "#cbd5e1",
							fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
							fontWeight: 500,
							whiteSpace: "nowrap",
						}}>
						{formatDateParam(from)} → {formatDateParam(to)} UTC
					</Typo>
				)}
			</HBox>
			<HBox gap="4px" sx={{ alignItems: "center" }}>
				{summary && from && to && (
					<Box
						onClick={() => saveSummaryPdf({
							periodFrom: formatDateParam(from),
							periodTo: formatDateParam(to),
							rows: buildSummaryPdfRows(summary),
						})}
						title={msg("downloadSummaryAsPdf")}
						sx={{
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							width: "22px",
							height: "22px",
							borderRadius: "5px",
							color: "#94a3b8",
							cursor: "pointer",
							transition: "background-color 0.15s, color 0.15s",
							"&:hover": { backgroundColor: "#f1f5f9", color: "#475569" },
						}}>
						<Download size={14} />
					</Box>
				)}
				{!summary && onDismissFallback && (
					<Box
						onClick={onDismissFallback}
						sx={{
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							width: "22px",
							height: "22px",
							borderRadius: "5px",
							color: "#94a3b8",
							cursor: "pointer",
							transition: "background-color 0.15s, color 0.15s",
							"&:hover": { backgroundColor: "#f1f5f9", color: "#475569" },
						}}>
						<X size={14} />
					</Box>
				)}
				{onCollapse && (
					<Box
						onClick={onCollapse}
						title={msg("collapseSummary")}
						sx={{
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							width: "22px",
							height: "22px",
							borderRadius: "5px",
							color: "#94a3b8",
							cursor: "pointer",
							transition: "background-color 0.15s, color 0.15s",
							"&:hover": { backgroundColor: "#f1f5f9", color: "#475569" },
						}}>
						<ChevronUp size={14} />
					</Box>
				)}
			</HBox>
		</HBox>
		{summary ? (
			// CSS grid with auto-fit + minmax lets the items behave like a responsive grid:
			// fits as many per row as horizontally possible, wraps the rest cleanly to the
			// next row (no awkward squishing below `min`). At ~6 items × 170px min + gaps,
			// this typically gives 6-in-a-row on wide viewports and 3+3 / 2+2+2 on narrower.
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
					gap: "16px 20px",
					alignItems: "stretch",
				}}>
				<SummaryItem label={msg("turnover_8ecaf1d7")}   amount={formatSummaryCents(summary.turnover)}   count={summary.turnover_count}   currencyPrefix="EUR" entry={SUMMARY_ENTRIES.turnover} />
				<SummaryItem label={msg("authorized")} amount={formatSummaryCents(summary.authorized)} count={summary.authorized_count} currencyPrefix="EUR" entry={SUMMARY_ENTRIES.authorized} />
				<SummaryItem label={msg("settled")}    amount={formatSummaryCents(summary.settled)}    count={summary.settled_count}    currencyPrefix="EUR" entry={SUMMARY_ENTRIES.settled} />
				{summary.settled_later_count > 0 && (
					<SummaryItem label={msg("settlesLater")} amount={formatSummaryCents(summary.settled_later)} count={summary.settled_later_count} currencyPrefix="EUR" entry={SUMMARY_ENTRIES.settled_later} />
				)}
				<SummaryItem label={msg("failed_031a8f0f")}     amount={formatSummaryCents(summary.failed)}     count={summary.failed_count}     currencyPrefix="EUR" entry={SUMMARY_ENTRIES.failed} />
				<SummaryItem label={msg("refunded")}   amount={formatSummaryCents(summary.refunded)}   count={summary.refunded_count}   currencyPrefix="EUR" entry={SUMMARY_ENTRIES.refunded} />
				<SummaryItem label={msg("rollingReserved")} amount={formatSummaryEur(summary.rolling_reserved)} currencyPrefix="EUR" entry={SUMMARY_ENTRIES.rolling} />
				<SummaryItem label={msg("fees_6d80486c")}             amount={formatSummaryEur(summary.fees)}             currencyPrefix="EUR" entry={SUMMARY_ENTRIES.fees} />
				<SummaryItem label={msg("net")}              amount={formatSummaryEur(summary.net)}              currencyPrefix="EUR" entry={SUMMARY_ENTRIES.net} />
			</Box>
		) : (
			<Typo
				sx={{
					fontSize: "12px",
					color: "#64748b",
					lineHeight: "18px",
				}}>{msg("summaryAggregatesAreComputedOnlyWhenNoFiltersAreAppliedStatu_ad68352b")}</Typo>
		)}
	</VBox>
)); };

const normalizeSessionPagination = (data: any, currentPage: number, itemsLength: number): IPagination | undefined => {
	if (data?.pagination) return data.pagination;

	if (data?.pages) {
		return {
			total: data.pages.total ?? 0,
			page: data.pages.current ?? currentPage,
			perPage: data.pages.perPage ?? perPage,
			totalPages: data.pages.totalPages ?? data.pages.total ?? 0,
		};
	}

	if (data) {
		const total = data.total ?? itemsLength;
		const page = data.page ?? currentPage;
		const perPageValue = data.perPage ?? perPage;
		return {
			total,
			page,
			perPage: perPageValue,
			totalPages: Math.ceil(total / perPageValue),
		};
	}

	return undefined;
};

const getCountryName = (code?: string | null) => {
	if (!hasDisplayValue(code)) return msg("unknownCountry");
	const normalizedCode = String(code).toUpperCase();
	return CountriesData.countries.find(country => country.alpha2 === normalizedCode)?.name ?? normalizedCode;
};

const getSessionRefundInfo = (row: IPaymentSession) => {
	const refund = row.children?.find(payment => payment.type?.toLowerCase() === "refund");
	const amount = row.refund_amount ?? refund?.amount;
	const status = row.refund_status ?? refund?.status;

	if (!hasDisplayValue(amount) && !hasDisplayValue(status)) return null;

	return {
		amount,
		status,
		currency: refund?.currency ?? row.currency,
	};
};

const normalizeMerchantFilterItems = (value: unknown): IMerchantFilterItem[] => {
	const items = Array.isArray(value)
		? value
		: value && typeof value === "object" && Array.isArray((value as { items?: unknown }).items)
			? (value as { items: unknown[] }).items
			: [];

	return items
		.filter((item): item is IMerchantFilterItem => typeof item === "object" && item !== null && typeof (item as IMerchantFilterItem).id === "number")
		.filter(item => item.id > 0 && item.status !== "deleted");
};

const getMerchantFilterLabel = (merchant?: IMerchantFilterItem) => {
	if (!merchant) return msg("allMerchants");
	if (hasDisplayValue(merchant.name)) return merchant.name;
	if (hasDisplayValue(merchant.domain)) return merchant.domain;
	return msg("templateMerchant_bf3dbf70", { v0: merchant.id });
};

const MerchantFilterControl = ({ merchant, onClick }: { merchant?: IMerchantFilterItem; onClick: () => void }) => { useLocale(); return ((
	<Box sx={{ position: "relative", minWidth: "190px", maxWidth: "260px" }}>
		<TypoCaption block>{msg("merchant_d2eb2cae")}</TypoCaption>
		<Spacer size="3px" />
		<HBox
			onClick={event => {
				event?.preventDefault();
				event?.stopPropagation();
				onClick();
			}}
			sx={{
				alignItems: "center",
				minWidth: 0,
				...InputBoxStyle(false),
				borderRadius: "4px",
				cursor: "pointer",
				overflow: "hidden",
				position: "relative",
			}}>
			<Box sx={{ ...InputFontStyle("small"), overflow: "hidden", flex: "1 1 auto", minWidth: 0 }}>
				<Typo sx={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", color: merchant ? "#334155" : "#94a3b8" }}>
					{getMerchantFilterLabel(merchant)}
				</Typo>
			</Box>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					cursor: "pointer",
					flexShrink: 0,
					marginLeft: "auto",
					width: "30px",
					"&>svg": { pointerEvents: "none", height: "24px", width: "24px" },
				}}>
				<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M17 9V11L12 16L7 11V9L12 14L17 9Z" fill="#000000CC" fillOpacity="1" />
				</svg>
			</Box>
		</HBox>
	</Box>
)); };

const MerchantFilterPopup = ({
	open,
	items,
	busy,
	error,
	search,
	selectedMerchant,
	onSearchChange,
	onSelect,
	onClose,
}: {
	open: boolean;
	items: IMerchantFilterItem[];
	busy: boolean;
	error?: string;
	search: { search: string };
	selectedMerchant?: IMerchantFilterItem;
	onSearchChange: (value: string) => void;
	onSelect: (merchant?: IMerchantFilterItem) => void;
	onClose: () => void;
}) => {
	useLocale();
	if (!open) return null;

	const searchValue = search.search.trim().toLowerCase();
	const filteredItems = searchValue
		? items.filter(merchant => {
				const haystack = [merchant.name, merchant.domain, merchant.id].filter(hasDisplayValue).join(" ").toLowerCase();
				return haystack.includes(searchValue);
			})
		: items;

	return createPortal(
		<Box
			onClick={onClose}
			sx={{
				position: "fixed",
				inset: 0,
				zIndex: 10000,
				backgroundColor: "rgba(15,23,42,0.22)",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				padding: "20px",
				boxSizing: "border-box",
			}}>
			<VBox
				onClick={event => event?.stopPropagation()}
				sx={{
					width: "min(520px, 100%)",
					maxHeight: "75vh",
					backgroundColor: "#fff",
					borderRadius: "14px",
					boxShadow: "0 22px 60px rgba(15,23,42,0.28), 0 6px 20px rgba(15,23,42,0.16)",
					overflow: "hidden",
				}}>
				<HBox sx={{ alignItems: "center", gap: "10px", padding: "16px 18px", borderBottom: "1px solid #f1f5f9" }}>
					<Typo variant="medium" bold sx={{ fontSize: "15px", color: "#1e293b" }}>{msg("selectMerchant")}</Typo>
					<Spacer grow />
					<Button onClick={onClose} title={msg("close")} variant="secondarySmall" />
				</HBox>

				<VBox sx={{ padding: "14px 18px", gap: "12px", minHeight: 0 }}>
					<Input size="small" type="search" placeholder={msg("search_7f553822")} name="search" onChange={onSearchChange} data={search} />

					<VBox sx={{ overflow: "auto", maxHeight: "52vh", gap: "6px" }}>
						<HBox
							onClick={() => onSelect(undefined)}
							sx={{
								padding: "10px 12px",
								border: "1px solid #cbd5e1",
								borderRadius: "8px",
								cursor: "pointer",
								backgroundColor: !selectedMerchant ? "#eef4ff" : "#fff",
								"&:hover": { backgroundColor: "#f1f5f9" },
							}}>
							<Typo variant="medium" sx={{ color: "#1e293b", fontSize: "13px", fontWeight: 600 }}>{msg("allMerchants")}</Typo>
						</HBox>

						{busy && (
							<Typo variant="medium" sx={{ color: "#94a3b8", padding: "12px" }}>{msg("loading")}</Typo>
						)}

						{error && !busy && (
							<Typo variant="medium" sx={{ color: "#dc2626", padding: "12px" }}>
								{error}
							</Typo>
						)}

						{!busy && !error && filteredItems.length === 0 && (
							<Typo variant="medium" sx={{ color: "#94a3b8", padding: "12px" }}>{msg("noMerchants")}</Typo>
						)}

						{!busy &&
							!error &&
							filteredItems.map(merchant => (
								<HBox
									key={merchant.id}
									onClick={() => onSelect(merchant)}
									sx={{
										padding: "10px 12px",
										border: "1px solid #cbd5e1",
										borderRadius: "8px",
										cursor: "pointer",
										alignItems: "center",
										gap: "12px",
										backgroundColor: selectedMerchant?.id === merchant.id ? "#eef4ff" : "#fff",
										transition: "background-color 0.15s, border-color 0.15s",
										"&:hover": { backgroundColor: "#f8fafc", borderColor: "#cbd5e1" },
									}}>
									<VBox sx={{ overflow: "hidden", gap: "2px" }}>
										{hasDisplayValue(merchant.name) && (
											<Typo
												variant="medium"
												sx={{
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
													fontSize: "13px",
													color: "#0f172a",
													fontWeight: 600,
												}}>
												{merchant.name}
											</Typo>
										)}
										{hasDisplayValue(merchant.domain) && (
											<Typo
												variant="caption"
												sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "11px", color: "#475569" }}>
												{merchant.domain}
											</Typo>
										)}
									</VBox>
									<Spacer grow />
									<Typo
										variant="caption"
										sx={{
											flexShrink: 0,
											color: "#334155",
											backgroundColor: "#e2e8f0",
											borderRadius: "6px",
											padding: "4px 8px",
											fontSize: "11px",
											fontVariantNumeric: "tabular-nums",
										}}>
										#{merchant.id}
									</Typo>
								</HBox>
							))}
					</VBox>
				</VBox>
			</VBox>
		</Box>,
		document.body,
	);
};

const SessionActionMenu = ({
	row,
	onForceWebhook,
	canChangeStatus,
}: {
	row: IPaymentSession;
	onForceWebhook: (id: number) => void;
	canChangeStatus: boolean;
}) => {
	useLocale();
	const [open, setOpen] = useState(false);
	const [menuPos, setMenuPos] = useState<{ top: number; left: number; openUp: boolean }>({ top: 0, left: 0, openUp: false });
	const ref = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;
		const handler = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node) && menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
		};
		const scrollHandler = () => setOpen(false);
		document.addEventListener("mousedown", handler);
		document.addEventListener("scroll", scrollHandler, true);
		return () => {
			document.removeEventListener("mousedown", handler);
			document.removeEventListener("scroll", scrollHandler, true);
		};
	}, [open]);

	const itemSx = {
		padding: "8px 16px",
		cursor: "pointer",
		whiteSpace: "nowrap" as const,
		transition: "background-color 0.15s ease",
		"&:hover": { backgroundColor: "#f0f5ff" },
	};

	return (
		<Box ref={ref} sx={{ position: "relative" }}>
			<Box
				onClick={e => {
					e?.stopPropagation();
					if (ref.current) {
						const rect = ref.current.getBoundingClientRect();
						const menuHeight = row.type !== "refund" ? 185 : 145;
						const openUp = rect.bottom + menuHeight > window.innerHeight;
						setMenuPos({
							top: openUp ? rect.top - menuHeight - 4 : rect.bottom + 4,
							left: rect.right,
							openUp,
						});
					}
					setOpen(v => !v);
				}}
				sx={{
					width: "32px",
					height: "32px",
					borderRadius: "8px",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					cursor: "pointer",
					transition: "background-color 0.15s",
					"&:hover": { backgroundColor: "rgba(0,0,0,0.06)" },
				}}>
				<svg width="18" height="18" viewBox="0 0 24 24" fill="#6b7280">
					<circle cx="12" cy="5" r="2" />
					<circle cx="12" cy="12" r="2" />
					<circle cx="12" cy="19" r="2" />
				</svg>
			</Box>
			{open &&
				createPortal(
					<VBox
						ref={menuRef}
						sx={{
							position: "fixed",
							top: menuPos.top + "px",
							left: menuPos.left + "px",
							transform: "translateX(-100%)",
							zIndex: 10000,
							backgroundColor: "#fff",
							borderRadius: "10px",
							boxShadow: "0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)",
							minWidth: "170px",
							overflow: "hidden",
							border: "1px solid #e5e7eb",
						}}>
						<Box
							sx={itemSx}
							onClick={e => {
								e?.stopPropagation();
								setOpen(false);
								savePdf(row);
							}}>
							<Typo variant="medium" sx={{ fontWeight: 500 }}>
								PDF
							</Typo>
						</Box>
						{canChangeStatus && (
							<>
								<Box sx={{ height: "1px", backgroundColor: "#f0f0f0", margin: "0 12px" }} />
								<Box
									sx={itemSx}
									onClick={e => {
										e?.stopPropagation();
										setOpen(false);
										S_CHANGE_SESSION_STATUS_POPUP.invoke({ sessionId: row.id, currentStatus: row.status });
									}}>
									<Typo variant="medium" sx={{ fontWeight: 500 }}>{msg("changeStatus_9ea1f4a2")}</Typo>
								</Box>
							</>
						)}
						<Box sx={{ height: "1px", backgroundColor: "#f0f0f0", margin: "0 12px" }} />
						<Box
							sx={itemSx}
							onClick={e => {
								e?.stopPropagation();
								setOpen(false);
								onForceWebhook(row.id);
							}}>
							<Typo variant="medium" sx={{ fontWeight: 500 }}>{msg("forceWebhook")}</Typo>
						</Box>
						{row.type !== "refund" && row.status === "completed" && (
							<>
								<Box sx={{ height: "1px", backgroundColor: "#f0f0f0", margin: "0 12px" }} />
								<Box
									sx={{ ...itemSx, "&:hover": { backgroundColor: "#fef2f2" } }}
									onClick={e => {
										e?.stopPropagation();
										setOpen(false);
										S_REFUND_POPUP.invoke({ amount: row.amount, sessionId: row.id, method: "gate/refund", currency: row.currency });
									}}>
									<Typo variant="medium" sx={{ fontWeight: 500, color: "#DC2626" }}>{msg("refund")}</Typo>
								</Box>
							</>
						)}
					</VBox>,
					document.body,
				)}
		</Box>
	);
};


// status → colors (bg / text / row-border) + label + small icon path.
// keep palette aligned with existing fee/amount colors so the table reads consistently.
type TStatusVisual = { bg: string; text: string; border: string; label: string; iconPath: string };
const STATUS_VISUALS: Record<string, TStatusVisual> = {
	completed: { bg: "#d6ecdd", text: "#14532d", border: "#e3eae5", get label() { return msg("completed"); }, iconPath: "M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" },
	pending: { bg: "#fffbeb", text: "#b45309", border: "#f59e0b", get label() { return msg("pending"); }, iconPath: "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16Zm.5-13H11v6l5.2 3.2.8-1.3-4.5-2.7z" },
	created: { bg: "#f1f5f9", text: "#475569", border: "#94a3b8", get label() { return msg("created"); }, iconPath: "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16Z" },
	failed: { bg: "#fecaca", text: "#7f1d1d", border: "#ef4444", get label() { return msg("failed_031a8f0f"); }, iconPath: "M19 6.4 17.6 5 12 10.6 6.4 5 5 6.4 10.6 12 5 17.6 6.4 19 12 13.4 17.6 19 19 17.6 13.4 12z" },
	expired: { bg: "#fff7ed", text: "#c2410c", border: "#f97316", get label() { return msg("expired"); }, iconPath: "M19 4h-1V2h-2v2H8V2H6v2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 16H5V10h14v10Z" },
	canceled: { bg: "#f1f5f9", text: "#475569", border: "#94a3b8", get label() { return msg("canceled"); }, iconPath: "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm-8 10c0-1.85.63-3.55 1.69-4.9L16.9 18.31A7.95 7.95 0 0 1 4 12Zm14.31 4.9L7.1 5.69A7.95 7.95 0 0 1 20 12c0 1.85-.63 3.55-1.69 4.9Z" },
	error: { bg: "#fecaca", text: "#7f1d1d", border: "#ef4444", get label() { return msg("error"); }, iconPath: "M12 2 1 21h22L12 2Zm1 14h-2v-2h2v2Zm0-4h-2V8h2v4Z" },
	unknown: { bg: "#f1f5f9", text: "#475569", border: "#94a3b8", get label() { return msg("unknown"); }, iconPath: "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm.5 17h-1v-2h1v2Zm1.3-6.1c-.5.3-.8.7-.8 1.3v.3h-1v-.5c0-.7.3-1.3 1.1-1.8.8-.4 1.2-.8 1.2-1.4 0-.7-.5-1.1-1.3-1.1-.7 0-1.2.4-1.3 1.1h-1.1c.1-1.3 1-2.1 2.4-2.1 1.5 0 2.4.9 2.4 2.1 0 .9-.5 1.5-1.6 2.1Z" },
};
const getStatusVisual = (status: string): TStatusVisual => STATUS_VISUALS[status] ?? STATUS_VISUALS.created;

// type → label + color + small icon path. Caption-style (no big pill).
type TTypeVisual = { label: string; color: string; iconPath: string };
const TYPE_VISUALS: Record<string, TTypeVisual> = {
	transfer: { get label() { return msg("transfer"); }, color: "#15803d", iconPath: "M4 11h12.2l-3.6-3.6L14 6l6 6-6 6-1.4-1.4 3.6-3.6H4z" },
	refund: { get label() { return msg("refund"); }, color: "#1d4ed8", iconPath: "M12 5V1L7 6l5 5V7c3.3 0 6 2.7 6 6 0 2-1 3.8-2.5 4.9l1.5 1.5C19.4 17.9 20 15.5 20 13c0-4.4-3.6-8-8-8Zm-6 8c0-2 1-3.8 2.5-4.9L7 6.6C5 8.1 4 10.5 4 13c0 4.4 3.6 8 8 8v4l5-5-5-5v4c-3.3 0-6-2.7-6-6Z" },
	oct: { get label() { return msg("oct"); }, color: "#6d28d9", iconPath: "M20 11H7.8l3.6-3.6L10 6l-6 6 6 6 1.4-1.4L7.8 13H20z" },
	adjustment_in: { get label() { return msg("adjustmentIn"); }, color: "#0e7490", iconPath: "M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z" },
	adjustment_out: { get label() { return msg("adjustmentOut"); }, color: "#b91c1c", iconPath: "M19 13H5v-2h14z" },
};
const getTypeVisual = (type?: string): TTypeVisual => {
	const key = (type || "").toLowerCase();
	if (TYPE_VISUALS[key]) return TYPE_VISUALS[key];
	// fallback: humanize unknown enum value (e.g. "weird_type" → "Weird Type") so we never
	// show a literally-typed-out raw string from the DB.
	const fallbackLabel = type
		? type.split("_").map(part => (part ? part[0].toUpperCase() + part.slice(1) : "")).join(" ")
		: "—";
	return { label: fallbackLabel, color: "#64748b", iconPath: "" };
};

// const StatusIcon = ({ status, size = 11 }: { status: string; size?: number }) => {
// 	const v = getStatusVisual(status);
// 	return (
// 		<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden style={{ flexShrink: 0 }}>
// 			<path d={v.iconPath} />
// 		</svg>
// 	);
// };

// Returns separate instrument + instrument_type icons (each in its own fixed-width slot
// by the caller) plus a composed tooltip label.
// `instrument` is the payment-method enum (card/ob/ap/gp/unknown) — primary slot.
// `instrument_type` is the card network (visa/mastercard) — secondary slot, rendered
// even when empty so the following Type label stays horizontally aligned across rows.
const getInstrumentDisplay = (row: IPaymentSession): { instrumentIcon: JSX.Element | null; typeIcon: JSX.Element | null; label: string } => {
	const labels: string[] = [];
	let instrumentIcon: JSX.Element | null = null;
	let typeIcon: JSX.Element | null = null;

	if (row.instrument === "ap") { instrumentIcon = <IconApple size="16px" />; labels.push("Apple Pay"); }
	else if (row.instrument === "gp") { instrumentIcon = <IconGoogle size="16px" />; labels.push("Google Pay"); }
	else if (row.instrument === "ob") { instrumentIcon = <IconBank size="16px" />; labels.push(row.institution ? "Open Banking — " + row.institution : msg("openBanking")); }
	else if (row.instrument === "card") { instrumentIcon = <IconCard size="16px" />; labels.push("Card"); }
	// 'unknown' / undefined → no instrument icon

	const instrumentType = row.instrument_type?.toLowerCase();
	if (instrumentType === "visa") { typeIcon = <IconVisa width={26} height={10} />; labels.push("Visa"); }
	else if (instrumentType === "mastercard") { typeIcon = <IconMastercard width={18} height={14} />; labels.push("Mastercard"); }

	return { instrumentIcon, typeIcon, label: labels.join(" · ") };
};

type TPillVisual = { bg: string; text: string; label?: string; iconPath?: string };

const StatusPill = ({ bg, text, label, iconPath }: TPillVisual) => {
	useLocale();
	const hasLabel = !!label;
	return (
		<Box
			sx={{
				display: "inline-flex",
				alignItems: "center",
				gap: hasLabel ? "4px" : 0,
				padding: hasLabel ? "1px 6px 1px 7px" : "2px 5px",
				borderRadius: "4px",
				backgroundColor: bg,
				color: text,
				lineHeight: "16px",
				whiteSpace: "nowrap",
			}}>
			{hasLabel && <span style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "0.1px" }}>{label}</span>}
			{iconPath && (
				<svg width={11} height={11} viewBox="0 0 24 24" fill="currentColor" aria-hidden style={{ flexShrink: 0 }}>
					<path d={iconPath} />
				</svg>
			)}
		</Box>
	);
};

// hourglass — used as the "pending / unknown settlement" indicator.
const HOURGLASS_ICON_PATH = "M6 2v6h.01L6 8.01 10 12l-4 3.99V22h12v-6.01L14 12l4-3.99V8h.01L18 8V2H6zm10 14.5V20H8v-3.5l4-4 4 4zM12 11 8 7V4h8v3l-4 4z";

// settlement_status pills are icon-only with their own palette decoupled from the auth-status palette:
// completed → blue ✓, failed → strong red × (darker than the auth-failed pill so the two reds are distinguishable
// when both pills sit side by side), anything else → very pale ⌛.
// Refund-status pill — shown ONLY for transactions that have a refund_status set
// (meaning this txn was the *target* of a refund, not a refund itself). Icon-only,
// reuses the rotating-arrow path from the refund type-visual. Colors are deliberately
// bright/saturated so the indicator pops next to the calmer auth/settlement pills.
const getRefundVisual = (refundStatus: string): TPillVisual => {
	const path = TYPE_VISUALS.refund.iconPath;
	if (refundStatus === "completed") return { bg: "#475569", text: "#ffffff", iconPath: path };
	if (refundStatus === "failed") return { bg: "#dc2626", text: "#ffffff", iconPath: path };
	// pending — amber, indicates refund in flight / awaiting processor confirmation
	return { bg: "#fbbf24", text: "#78350f", iconPath: path };
};

const getSettlementVisual = (settlementStatus: string): TPillVisual => {
	if (settlementStatus === "completed") {
		return { bg: "#bfdbfe", text: "#1e3a8a", iconPath: STATUS_VISUALS.completed.iconPath };
	}
	if (settlementStatus === "failed") {
		return { bg: "#dc2626", text: "#ffffff", iconPath: STATUS_VISUALS.failed.iconPath };
	}
	return { bg: "#e2e8f0", text: "#475569", iconPath: HOURGLASS_ICON_PATH };
};

// Tail of the "auth → settlement" flow shown in the tooltip.
// Picks wording from settlement_status first (definitive), falls back to auth-status when
// settlement hasn't progressed (so a failed auth reads "Not settled" instead of "Waiting for settlement").
const getStatusFlowTail = (row: IPaymentSession, settlementDate: string): string => {
	const ss = row.settlement_status;
	if (ss === "completed") return settlementDate !== "—" ? msg("templateSettledAt_bbf98c76", { v0: settlementDate }) : "Settled";
	if (ss === "failed") return msg("notSettled");
	if (row.status === "completed") return msg("waitingForSettlement");
	if (row.status === "failed" || row.status === "expired" || row.status === "error") return msg("notSettled");
	if (row.status === "canceled") return msg("noSettlement");
	if (row.status === "pending" || row.status === "created") return msg("awaitingAuthorization");
	return msg("waitingForSettlement");
};

export const SessionStatusCell = ({ row, cell }: { row: IPaymentSession; cell: any }) => {
	useLocale();
	const isFailed = row.status === "failed";
	const v = getStatusVisual(row.status);
	const authLabel = v.label ?? (typeof cell === "string" ? cell : row.status);

	const settlementStatus = String(row.settlement_status ?? "unknown");
	const settlementVisual = getSettlementVisual(settlementStatus);
	const settlementDate = formatUtcDateOnly(row.settlement_date);
	const flowTail = getStatusFlowTail(row, settlementDate);

	const refundInfo = getSessionRefundInfo(row);
	const refundStatus = refundInfo && hasDisplayValue(refundInfo.status) ? String(refundInfo.status).toLowerCase() : "";
	const refundVisual = refundStatus ? getRefundVisual(refundStatus) : null;

	const content = (
		<VBox sx={{ gap: "3px", alignItems: "flex-start" }}>
			<HBox gap="5px" sx={{ alignItems: "center" }}>
				<StatusPill bg={v.bg} text={v.text} label={authLabel} iconPath={v.iconPath} />
				<StatusPill {...settlementVisual} />
				{refundVisual ? <StatusPill {...refundVisual} /> : null}
			</HBox>
			<Typo
				variant="caption"
				sx={{ display: "block", color: "#475569", opacity: 1, fontSize: "11px", lineHeight: "14px", whiteSpace: "nowrap" }}>
				{settlementDate !== "—" ? (
					<>
						<span style={{ color: "#94a3b8" }}>{msg("settlement")}</span> {settlementDate}
					</>
				) : (
					<span style={{ color: "#94a3b8" }}>{msg("notSettled_bfe1f62e")}</span>
				)}
			</Typo>
		</VBox>
	);

	const failReason = isFailed && row.fail_reason ? row.fail_reason : null;
	const refundAmountText = refundInfo && hasDisplayValue(refundInfo.amount)
		? `${(Number(refundInfo.amount) / 100).toFixed(2)} ${refundInfo.currency ?? ""}`.trim()
		: "";
	const tooltipContent = (
		<Typo variant="caption" sx={{ color: "#FFFFFF", opacity: 1, fontSize: "11px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
			<div style={{ fontWeight: 600 }}>
				{authLabel} <span style={{ opacity: 0.6, margin: "0 4px" }}>→</span> {flowTail}
			</div>
			{refundVisual && (
				<div style={{ marginTop: "4px", color: "rgba(255,255,255,0.85)" }}>{msg("refund_d272d5b4")}{refundStatus}
					{refundAmountText ? ` (${refundAmountText})` : ""}
				</div>
			)}
			{failReason && (
				<div style={{ marginTop: "6px", color: "rgba(255,255,255,0.78)" }}>
					{failReason.includes(",") ? failReason.split(",").map((part, i) => <div key={i}>{part.trim()}</div>) : failReason}
				</div>
			)}
		</Typo>
	);

	return (
		<HoverTooltip align="end" style={{ cursor: "help" }} tooltipStyle={{ maxWidth: "500px", width: "max-content" }} content={tooltipContent}>
			{content}
		</HoverTooltip>
	);
};

export const SessionTypeCell = ({ row }: { row: IPaymentSession }) => {
	useLocale();
	const type = row.type?.toLowerCase();
	const v = getTypeVisual(type);
	const instrumentDisplay = getInstrumentDisplay(row);

	// fixed-width slots so the Type label aligns horizontally across rows regardless of
	// which icon (or none) is present. Widths chosen to fit the widest icon in each slot
	// (instrument: 16px square icons; type: Visa logo 26px wide).
	const instrumentSlotSx = { display: "inline-flex", alignItems: "center", justifyContent: "center", width: "20px", height: "16px", flexShrink: 0, "& svg": { display: "block" } } as const;
	const typeSlotSx = { display: "inline-flex", alignItems: "center", justifyContent: "center", width: "28px", height: "16px", flexShrink: 0, "& svg": { display: "block" } } as const;

	const isSandbox = row.sandbox > 0;
	const cellContent = (
		<VBox sx={{ gap: "2px", minWidth: 0 }}>
			<HBox gap="6px" sx={{ alignItems: "center", flexWrap: "nowrap", whiteSpace: "nowrap" }}>
				<Box sx={instrumentSlotSx}>{instrumentDisplay.instrumentIcon}</Box>
				<Box sx={typeSlotSx}>{instrumentDisplay.typeIcon}</Box>
				{v.iconPath && (
					<svg width={13} height={13} viewBox="0 0 24 24" fill={v.color} aria-hidden style={{ flexShrink: 0 }}>
						<path d={v.iconPath} />
					</svg>
				)}
				<Typo sx={{ color: v.color, opacity: 1, fontWeight: 600, fontSize: "13px", lineHeight: "16px" }}>
					{v.label}
				</Typo>
			</HBox>
			{isSandbox && (
				<Typo
					variant="caption"
					sx={{
						display: "block",
						color: "#c2410c",
						opacity: 1,
						fontSize: "10px",
						lineHeight: "14px",
						fontWeight: 700,
						letterSpacing: "0.8px",
						textTransform: "uppercase",
						whiteSpace: "nowrap",
					}}>{msg("sandbox_b7ad5674")}</Typo>
			)}
		</VBox>
	);

	// Wrap in tooltip so the instrument label (Apple Pay / Visa / etc.) is reachable on
	// hover without spending a separate column for it.
	if (!instrumentDisplay.label) return cellContent;
	return (
		<HoverTooltip
			align="center"
			tooltipStyle={{ width: "max-content" }}
			content={
				<Typo variant="caption" sx={{ color: "#FFFFFF", opacity: 1, fontSize: "11px", whiteSpace: "nowrap" }}>
					{instrumentDisplay.label}
				</Typo>
			}>
			{cellContent}
		</HoverTooltip>
	);
};

export const SessionAmountCell = ({ row }: { row: IPaymentSession }) => {
	useLocale();
	const type = row.type?.toLowerCase();
	const isRefund = type === "refund";
	const isDmsOrTransfer = type === "dms" || type === "transfer";
	const amountColor = isRefund ? "#DC2626" : isDmsOrTransfer ? "#16A34A" : "#1e293b";
	const prefix = isRefund ? "-" : "";
	return (
		<Typo
			variant="medium"
			sx={{
				color: amountColor,
				fontWeight: 600,
				fontSize: "13px",
				fontVariantNumeric: "tabular-nums",
				whiteSpace: "nowrap",
				textAlign: "end",
				display: "block",
			}}>
			{prefix + (row.amount / 100).toFixed(2)}
			<span style={{ color: "#475569", fontWeight: 400, marginLeft: "4px" }}>{row.currency ?? ""}</span>
		</Typo>
	);
};

const SessionEmailCell = ({ row }: { row: IPaymentSession }) => { useLocale(); return ((
	<VBox sx={{ width: "100%", minWidth: 0, gap: "2px" }}>
		<Typo sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "13px", lineHeight: "16px", minWidth: 0 }} variant="medium">
			{row.email ?? ""}
		</Typo>
		{(row.country || row.ip) && (
			<HBox gap="8px" sx={{ alignItems: "center", minWidth: 0, height: "14px" }}>
				{row.country ? (
					<HoverTooltip
						align="center"
						tooltipStyle={{ width: "max-content" }}
						content={
							<Typo variant="caption" sx={{ color: "#FFFFFF", opacity: 1, fontSize: "11px", whiteSpace: "nowrap" }}>
								{getCountryName(row.country)}
							</Typo>
						}>
						<Box sx={{ display: "inline-block", width: "18px", height: "12px", flexShrink: 0, overflow: "hidden" }}>
							<Box sx={{ transform: "scale(0.75)", transformOrigin: "top left" }}>
								<CountryFlag code={row.country} variant="rectangle" />
							</Box>
						</Box>
					</HoverTooltip>
				) : null}
				{row.ip && (
					<Typo
						variant="caption"
						sx={{ display: "block", flex: 1, color: "#475569", opacity: 1, fontSize: "11px", lineHeight: "14px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
						{row.ip}
					</Typo>
				)}
			</HBox>
		)}
	</VBox>
)); };

const SessionInstrumentCell = ({ row }: { row: IPaymentSession }) => {
	useLocale();
	// Type+instrument info (Apple Pay / Visa / Mastercard / Card) now lives in the merged
	// Type column. This cell focuses on *where* the instrument is anchored: issuing
	// institution (top) and the card's country + masked name underneath.
	const institution = hasDisplayValue(row.institution) ? String(row.institution) : "";
	const instrumentName = hasDisplayValue(row.instrument_name) ? String(row.instrument_name) : "";
	const instrumentCountry = hasDisplayValue(row.instrument_country) ? String(row.instrument_country) : "";
	const instrumentDisplay = getInstrumentDisplay(row);
	const countryName = instrumentCountry ? getCountryName(instrumentCountry) : "";

	const hasTooltipContent = institution || instrumentDisplay.label || instrumentName || instrumentCountry;

	const cellContent = (
		<VBox sx={{ minWidth: 0, gap: "2px", cursor: hasTooltipContent ? "help" : undefined }}>
			{institution ? (
				<Typo sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "13px", lineHeight: "16px", minWidth: 0 }} variant="medium">
					{institution}
				</Typo>
			) : null}
			{instrumentCountry || instrumentName ? (
				<HBox gap="8px" sx={{ alignItems: "center", minWidth: 0, height: "14px" }}>
					{instrumentCountry ? (
						<Box sx={{ display: "inline-block", width: "18px", height: "12px", flexShrink: 0, overflow: "hidden" }}>
							<Box sx={{ transform: "scale(0.75)", transformOrigin: "top left" }}>
								<CountryFlag code={instrumentCountry} variant="rectangle" />
							</Box>
						</Box>
					) : null}
					{instrumentName ? (
						<Typo
							variant="caption"
							sx={{ display: "block", flex: 1, color: "#475569", opacity: 1, fontSize: "11px", lineHeight: "14px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
							{instrumentName}
						</Typo>
					) : null}
				</HBox>
			) : null}
		</VBox>
	);

	if (!hasTooltipContent) return cellContent;

	const tooltipContent = (
		<VBox sx={{ gap: "6px", maxWidth: "340px", minWidth: 0 }}>
			{institution ? (
				<Typo variant="caption" sx={{ color: "#FFFFFF", opacity: 1, fontSize: "12px", fontWeight: 600, whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: "16px" }}>
					{institution}
				</Typo>
			) : null}
			{(instrumentDisplay.instrumentIcon || instrumentDisplay.typeIcon || instrumentDisplay.label) ? (
				<HBox gap="8px" sx={{ alignItems: "center", flexWrap: "wrap", paddingTop: institution ? "4px" : 0, borderTop: institution ? "1px solid rgba(255,255,255,0.15)" : undefined }}>
					{instrumentDisplay.instrumentIcon ? (
						<Box sx={{ display: "inline-flex", alignItems: "center", color: "#FFFFFF" }}>{instrumentDisplay.instrumentIcon}</Box>
					) : null}
					{instrumentDisplay.typeIcon ? (
						<Box sx={{ display: "inline-flex", alignItems: "center" }}>{instrumentDisplay.typeIcon}</Box>
					) : null}
					{instrumentDisplay.label ? (
						<Typo variant="caption" sx={{ color: "#FFFFFF", opacity: 1, fontSize: "11px", whiteSpace: "nowrap" }}>
							{instrumentDisplay.label}
						</Typo>
					) : null}
				</HBox>
			) : null}
			{instrumentName ? (
				<Typo variant="caption" sx={{ color: "#E2E8F0", opacity: 1, fontSize: "11px", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", letterSpacing: "0.3px" }}>
					{instrumentName}
				</Typo>
			) : null}
			{instrumentCountry ? (
				<HBox gap="6px" sx={{ alignItems: "center" }}>
					<Box sx={{ display: "inline-block", width: "18px", height: "12px", flexShrink: 0, overflow: "hidden" }}>
						<Box sx={{ transform: "scale(0.75)", transformOrigin: "top left" }}>
							<CountryFlag code={instrumentCountry} variant="rectangle" />
						</Box>
					</Box>
					<Typo variant="caption" sx={{ color: "#FFFFFF", opacity: 1, fontSize: "11px", whiteSpace: "nowrap" }}>
						{countryName || instrumentCountry}
					</Typo>
				</HBox>
			) : null}
		</VBox>
	);

	return (
		<HoverTooltip align="center" tooltipStyle={{ width: "max-content", maxWidth: "360px" }} content={tooltipContent}>
			{cellContent}
		</HoverTooltip>
	);
};


const formatFeeCellValue = (value: number | string | null | undefined) => {
	if (value === null || value === undefined || value === "") return "-";
	return typeof value === "number" ? formatAmount(value) : value;
};

const hasFeeCellValue = (value: number | string | null | undefined) => value !== null && value !== undefined && value !== "";

// settlement_date in DB is stored in UTC (server is UTC); show as plain YYYY-MM-DD.
// For "YYYY-MM-DD HH:MM:SS" strings we just slice — no Date round-trip that could shift
// the day under local timezone parsing rules.
const formatUtcDateOnly = (value: string | number | null | undefined): string => {
	if (value === null || value === undefined || value === "") return "—";
	if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.substring(0, 10);
	const d = new Date(value as any);
	return isNaN(d.getTime()) ? "—" : d.toISOString().substring(0, 10);
};

// Reusable "no data yet" tooltip body — used by both Reserved and Fee cells when the
// underlying value hasn't been populated (settlement hasn't fired).
const pendingSettlementHint = () => (
	<Typo variant="caption" sx={{ color: "#FFFFFF", opacity: 1, fontSize: "11px", whiteSpace: "nowrap" }}>{msg("dataWillAppearAfterSettlement")}</Typo>
);

export const SessionReservedCell = ({ row }: { row: IPaymentSession }) => {
	useLocale();
	const value = row.rolling_reserve_amount;
	const has = hasFeeCellValue(value);
	const percent = (row as any).rolling_reserver_percent;
	const tooltipContent = has ? (
		<Typo variant="caption" sx={{ color: "#FFFFFF", opacity: 1, fontSize: "11px", whiteSpace: "nowrap" }}>{msg("rollingReserved_99a1208a")}{percent !== null && percent !== undefined ? percent : "—"}{msg("from")}{(Number(row.amount) / 100).toFixed(2)} {row.currency ?? ""}
		</Typo>
	) : pendingSettlementHint();

	return (
		<HoverTooltip align="end" tooltipStyle={{ width: "max-content" }} content={tooltipContent}>
			<Typo
				variant="medium"
				sx={{ fontSize: "13px", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", textAlign: "end", display: "block", cursor: "help" }}>
				{formatFeeCellValue(value)}
				{has && <span style={{ color: "#475569", fontWeight: 400, marginLeft: "4px" }}>{row.currency ?? ""}</span>}
			</Typo>
		</HoverTooltip>
	);
};

export const SessionFeeCell = ({ row }: { row: IPaymentSession }) => {
	useLocale();
	const fc = (row as any).fee_conditions;
	let parsed: any = null;
	if (fc) {
		try { parsed = typeof fc === "string" ? JSON.parse(fc) : fc; } catch { parsed = null; }
	}

	// If fee_conditions exists but fees_amount is empty, render 0 — the conditions imply
	// fees were calculated (possibly resulting in zero) rather than "not calculated yet".
	const rawValue = row.fees_amount;
	const displayValue = !hasFeeCellValue(rawValue) && parsed ? 0 : rawValue;
	const showCurrency = hasFeeCellValue(displayValue);

	const tooltipContent = parsed ? (
		<VBox gap="2px" sx={{ maxWidth: "320px" }}>
			{Object.entries(parsed).map(([k, v]) => (
				<Typo
					key={k}
					variant="caption"
					sx={{ color: "#FFFFFF", opacity: 1, fontSize: "11px", whiteSpace: "normal", wordBreak: "break-word" }}>
					<span style={{ opacity: 0.7 }}>{k}:</span> {typeof v === "object" ? JSON.stringify(v) : String(v)}
				</Typo>
			))}
		</VBox>
	) : pendingSettlementHint();

	return (
		<HoverTooltip align="end" tooltipStyle={{ width: "max-content" }} content={tooltipContent}>
			<Typo
				variant="medium"
				sx={{ fontSize: "13px", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", textAlign: "end", display: "block", cursor: "help" }}>
				{formatFeeCellValue(displayValue)}
				{showCurrency && <span style={{ color: "#475569", fontWeight: 400, marginLeft: "4px" }}>{row.currency ?? ""}</span>}
			</Typo>
		</HoverTooltip>
	);
};

export const SessionMerchantCell = ({ row }: { row: IPaymentSession }) => { useLocale(); return ((
	<VBox sx={{ gap: "2px", minWidth: 0, width: "100%" }}>
		{hasDisplayValue(row.merchant_name) && (
			<Typo variant="medium" sx={{ display: "block", whiteSpace: "nowrap", fontSize: "13px", lineHeight: "16px", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
				{row.merchant_name}
			</Typo>
		)}
		{hasDisplayValue(row.processing_mid) && (
			<HBox gap="8px" sx={{ alignItems: "center", minWidth: 0, height: "14px" }}>
				<Typo
					variant="caption"
					sx={{ display: "block", flex: 1, color: "#475569", opacity: 1, fontSize: "11px", lineHeight: "14px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
					{row.processing_mid}
				</Typo>
			</HBox>
		)}
	</VBox>
)); };

// Optional bonus column shown only when there's ample horizontal room.
// Always rendered (a thin "—" when empty) so the grid layout stays stable across rows.
const SessionFailReasonCell = ({ row }: { row: IPaymentSession }) => {
	useLocale();
	const reasonText = hasDisplayValue(row.fail_reason) ? String(row.fail_reason) : "";
	if (!reasonText) {
		return (
			<Typo variant="caption" sx={{ display: "block", color: "#cbd5e1", opacity: 1, fontSize: "12px", textAlign: "start" }}>
				—
			</Typo>
		);
	}
	return (
		<HoverTooltip
			align="center"
			tooltipStyle={{ maxWidth: "500px", width: "max-content" }}
			content={
				<Typo variant="caption" sx={{ color: "#FFFFFF", opacity: 1, fontSize: "11px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
					{reasonText.includes(",") ? reasonText.split(",").map((part, i) => <div key={i}>{part.trim()}</div>) : reasonText}
				</Typo>
			}>
			<Typo
				variant="caption"
				sx={{
					display: "block",
					color: "#b91c1c",
					opacity: 1,
					fontSize: "12px",
					lineHeight: "16px",
					whiteSpace: "nowrap",
					overflow: "hidden",
					textOverflow: "ellipsis",
					cursor: "help",
					minWidth: 0,
				}}>
				{reasonText}
			</Typo>
		</HoverTooltip>
	);
};

export const SessionIdCell = ({ row }: { row: IPaymentSession }) => {
	useLocale();
	const dateLabel = formatDisplayDateTime(row.created_at, { date: { day: "2-digit", month: "2-digit", year: "2-digit" }, time: { hour: "2-digit", minute: "2-digit" }, separator: " " });
	const item = (
		<VBox sx={{ gap: "2px" }}>
			<Typo variant="medium" sx={{ whiteSpace: "nowrap", fontSize: "13px" }}>
				<DateTimeTooltip value={row.created_at}>{dateLabel}</DateTimeTooltip>
			</Typo>
			<Typo
				variant="caption"
				onClick={e => {
					e.stopPropagation();
					navigator.clipboard.writeText(row.id.toString());
					notify.copied();
				}}
				sx={{
					color: "#475569",
					opacity: 1,
					fontSize: "11px",
					cursor: "pointer",
					width: "max-content",
					borderRadius: "3px",
					padding: "1px 4px",
					marginLeft: "-4px",
					transition: "all 0.15s ease",
					"&:hover": { backgroundColor: "rgba(0,0,0,0.06)", color: "#1e293b" },
				}}>
				{row.id}
			</Typo>
		</VBox>
	);
	return item;
};

export const Sessions: TFeaturedComponent<TSessionProps> = ({
	target_id,
	target_type,
	filterable = true,
	filterAppearance = "default",
	types,
	tableScrollable = false,
	hideMerchantColumn,
	fixedFilters,
	requestOptions,
	showSummary = true,
	onOpenSession,
	surface = "#f8fafc",
}) => {
	useLocale();
	const [searchObject, setSearchObject] = useState<{ search: string }>({ search: "" });
	const [currentPage, setCurrentPage] = useState<number>(0);
	const [csvExportJob, setCsvExportJob] = useState<CursorExportJob<IPaymentSession> | null>(null);
	const [batchSnapshot, setBatchSnapshot] = useState<TransactionBatchSnapshot | null>(null);
	const [statusFilter, setStatusFilter] = useState<IFilterOption | undefined>(statusOptions[0]);
	const [settlementStatusFilter, setSettlementStatusFilter] = useState<IFilterOption | undefined>(settlementStatusOptions[0]);
	const [settlementDate, setSettlementDate] = useState("");
	const [typeFilter, setTypeFilter] = useState<IFilterOption | undefined>(typeOptions[0]);
	const [instrumentFilter, setInstrumentFilter] = useState<IFilterOption | undefined>(instrumentOptions[0]);
	const [merchantFilter, setMerchantFilter] = useState<IMerchantFilterItem | undefined>();
	const [merchantFilterItems, setMerchantFilterItems] = useState<IMerchantFilterItem[]>([]);
	const [merchantFilterOpen, setMerchantFilterOpen] = useState(false);
	const [merchantFilterBusy, setMerchantFilterBusy] = useState(false);
	const [merchantFilterError, setMerchantFilterError] = useState<string | undefined>();
	const [merchantSearch, setMerchantSearch] = useState<{ search: string }>({ search: "" });
	const [fromDate, setFromDate] = useState<Date>(defaultFromDate());
	const [toDate, setToDate] = useState<Date>(defaultToDate());
	const scrollRef = useRef<HTMLDivElement>(null);
	const topStickyRef = useRef<HTMLDivElement>(null);
	const [topStickyHeight, setTopStickyHeight] = useState(0);
	// User can dismiss the "summary unavailable" panel when filters block aggregation.
	// Auto-resets when filters clear (summary becomes non-null), so the next time filters
	// are re-applied and summary is null again, the panel reappears.
	const [summaryFallbackDismissed, setSummaryFallbackDismissed] = useState(false);
	// Collapse states: independent toggles for the filter panel and the summary panel.
	// When BOTH are collapsed, they merge into a single compact row.
	// Persisted in localStorage so the user's preference survives reloads + nav. Global
	// keys (not target-scoped) — if a user prefers compact mode on one Sessions table,
	// they almost certainly want it on every other Sessions table too.
	const [filterCollapsed, setFilterCollapsed] = useState<boolean>(() => {
		try { return localStorage.getItem(LS_FILTER_COLLAPSED_KEY) === "1"; }
		catch { return false; }
	});
	const [summaryCollapsed, setSummaryCollapsed] = useState<boolean>(() => {
		try { return localStorage.getItem(LS_SUMMARY_COLLAPSED_KEY) === "1"; }
		catch { return false; }
	});
	useEffect(() => {
		try { localStorage.setItem(LS_FILTER_COLLAPSED_KEY, filterCollapsed ? "1" : "0"); }
		catch { /* localStorage disabled / quota — collapse still works in-session */ }
	}, [filterCollapsed]);
	useEffect(() => {
		try { localStorage.setItem(LS_SUMMARY_COLLAPSED_KEY, summaryCollapsed ? "1" : "0"); }
		catch { /* same */ }
	}, [summaryCollapsed]);
	const [collapsedTableColumns, setCollapsedTableColumns] = useState<TCollapsibleSessionColumn[]>([]);
	const [headerHeight] = GD.B_HEADER_HEIGHT.use(undefined, undefined, []);
	const [project] = GD.B_SELECTED_PROJECT.use();
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined, [project]);
	const canFilterByMerchant = filterable && (target_type === "project" || target_type === "client");
	const userProject = project && user ? user.projects.find(val => val.id === project.id) : undefined;
	const canChangeSessionStatus = hasRole(userProject?.roles, "admin");
	const shouldHideMerchantColumn = hideMerchantColumn ?? (target_type === "merchant" && Number(target_id) > 0);

	// const theme = useTheme();
	// const style = theme.table ?? (theme.name === "light" ? getDefaultTableLightTheme : getDefaultTableDarkTheme);

	// const [startDate, setStartDate] = useState<Date>(() => {
	// 	const date = new Date();
	// 	// Tomorrow
	// 	date.setDate(date.getDate() + 1);
	// 	return date;
	// });

	//let isMerchant = true;

	// if (project && user) {
	// 	const userProject = user.projects.find(val => val.id === project.id);
	// 	if (userProject) {
	// 		const userRoles = userProject.roles.map(r => r.name);

	// 		if (userRoles.includes("admin")) {
	// 			isMerchant = false;
	// 		} else if (userRoles.includes("aml")) {
	// 			isMerchant = false;
	// 		} else if (userRoles.includes("merchant")) {
	// 			isMerchant = true;
	// 		}
	// 	}
	// }

	const getMethod = () => {
		return "session/list";
	};

	const getFilterParams = () => {
		const params: Record<string, string | string[] | number | number[]> = {};

		if (types) params.type = types;

		if (!filterable) return params;

		if (!searchObject.search) {
			params.from = formatDateParam(fromDate);
			params.to = formatDateParam(toDate);
		}

		if (statusFilter?.value) params.status = statusFilter.value;
		if (settlementStatusFilter?.value) params.settlement_status = settlementStatusFilter.value;
		if (settlementDate) params.settlement_date = settlementDate;
		if (typeFilter?.value) params.type = [typeFilter.value];
		if (instrumentFilter?.value) params.instrument = instrumentFilter.value;
		if (canFilterByMerchant && merchantFilter?.id) params.mids = [merchantFilter.id];

		return params;
	};

	const getRequestData = (page: number) => {

		return {
			target_id: target_id,
			target_type: target_type,
			search: filterable && searchObject.search ? searchObject.search : undefined,
			...getFilterParams(),
			pagination: {
				page: page,
				perPage: perPage,
			},
		};
	};

	const getRequest = (page: number, extraData?: Record<string, unknown>) => {
		return {
			method: getMethod(),
			...requestOptions,
			data: {
				...getRequestData(page),
				...extraData,
				...fixedFilters,
			},
		};
	};

	const [response, reload, _reset, _busy] = GD.REQ_API.useRequest(null, getRequest(currentPage));

	useEffect(() => {
		const handler = GD.S_TRANSACTIONS_UPDATE.subscribe(() => {
			reload(getRequest(currentPage));
		});
		return () => {
			GD.S_TRANSACTIONS_UPDATE.unsubscribe(handler);
		};
	}, [
		canFilterByMerchant,
		currentPage,
		filterable,
		fixedFilters,
		requestOptions,
		fromDate,
		instrumentFilter?.value,
		merchantFilter?.id,
		reload,
		searchObject.search,
		statusFilter?.value,
		settlementStatusFilter?.value,
		settlementDate,
		target_id,
		target_type,
		toDate,
		typeFilter?.value,
		types,
	]);

	useEffect(() => {
		setMerchantFilter(undefined);
		setMerchantFilterItems([]);
		setMerchantSearch({ search: "" });
	}, [target_id, target_type]);

	const getMerchantFilterRequest = () => {
		if (target_type === "client") {
			return {
				method: "merchants/list",
				data: {
					client_id: target_id,
				},
			};
		}

		return {
			method: "clients/group/list",
			data: {},
		};
	};

	const openMerchantFilter = async () => {
		if (!canFilterByMerchant) return;

		setMerchantFilterOpen(true);
		setMerchantSearch({ search: "" });
		setMerchantFilterBusy(true);
		setMerchantFilterError(undefined);

		try {
			const res = await GD.REQ_API.request(getMerchantFilterRequest());
			if (res?.error) {
				setMerchantFilterError(res.error.message ?? msg("unableToLoadMerchants"));
				setMerchantFilterItems([]);
				return;
			}

			setMerchantFilterItems(normalizeMerchantFilterItems(res?.data));
		} finally {
			setMerchantFilterBusy(false);
		}
	};

	const selectMerchantFilter = (merchant?: IMerchantFilterItem) => {
		setMerchantFilter(merchant);
		setMerchantFilterOpen(false);
	};

	const handleSearch = () => {
		setCurrentPage(0);
		reload(getRequest(0));
		setSearchObject({ search: searchObject.search });
	};

	const resetSearch = () => {
		const nextFromDate = defaultFromDate();
		const nextToDate = defaultToDate();

		setSearchObject({ search: "" });
		setStatusFilter(statusOptions[0]);
		setSettlementStatusFilter(settlementStatusOptions[0]);
		setSettlementDate("");
		setTypeFilter(typeOptions[0]);
		setInstrumentFilter(instrumentOptions[0]);
		setMerchantFilter(undefined);
		setFromDate(nextFromDate);
		setToDate(nextToDate);
		setCurrentPage(0);
		reload(
			getRequest(0, {
				search: undefined,
				status: undefined,
				settlement_status: undefined,
				settlement_date: undefined,
				type: undefined,
				instrument: undefined,
				mids: undefined,
				from: formatDateParam(nextFromDate),
				to: formatDateParam(nextToDate),
			}),
		);
	};

	const handleRefresh = () => {
		reload(getRequest(currentPage));
	};

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		reload(getRequest(page));
	};

	const handleDownloadCsv = () => {
		if (csvExportJob) return;
		const request = getRequest(0);
		const exportProjectId = requestOptions?.expectedProjectId ?? project?.id;
		const fileNameParts = ["sessions", sanitizeFileNamePart(String(target_type))];
		if (target_id != null) {
			fileNameParts.push(sanitizeFileNamePart(String(target_id)));
		}
		if (searchObject.search) {
			fileNameParts.push(sanitizeFileNamePart(searchObject.search));
		}
		if (statusFilter?.value) {
			fileNameParts.push(`status_${sanitizeFileNamePart(statusFilter.value)}`);
		}
		if (settlementStatusFilter?.value) {
			fileNameParts.push(`settle_${sanitizeFileNamePart(settlementStatusFilter.value)}`);
		}
		if (settlementDate) {
			fileNameParts.push(`settlement_date_${settlementDate}`);
		}
		if (typeFilter?.value) {
			fileNameParts.push(`type_${sanitizeFileNamePart(typeFilter.value)}`);
		}
		if (instrumentFilter?.value) {
			fileNameParts.push(`instrument_${sanitizeFileNamePart(instrumentFilter.value)}`);
		}
		if (merchantFilter?.id) {
			fileNameParts.push(`merchant_${sanitizeFileNamePart(String(merchantFilter.id))}`);
		}
		fileNameParts.push(formatDateParam(fromDate));
		fileNameParts.push(formatDateParam(toDate));

		setCsvExportJob({
			filename: fileNameParts.join("_") + ".csv",
			loadPage: async (cursor, signal) => {
				requestOptions?.signal?.throwIfAborted();
				const res = await GD.REQ_API.request({ ...request, expectedProjectId: exportProjectId, signal,
					data: sessionExportRequestData(request.data, cursor) });
				if (res.error) throw new globalThis.Error(res.error.message || msg("couldNotLoadTransactions"));
				return parseSessionExportPage<IPaymentSession>(res.data);
			},
			getRowKey: row => row.id,
			buildFile: collected => {
				const rows = collected.map(row => ({
					...row,
					statement_id: row.statement_info?.statement_id ?? "",
					statement_status: row.statement_info?.status ?? "",
				})) as unknown as Record<string, unknown>[];

				if (!rows.length) throw new globalThis.Error(msg("noTransactionsToExport"));

				const headers = getSessionCsvHeaders(rows);
				const csvRows = rows.map(row => headers.map(header => escapeCsvCell(getSessionCsvCellValue(header, row[header]))).join(","));
				const csv = [headers.join(","), ...csvRows].join("\n");
				return new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
			},
		});
	};

	const items = normalizeSessionItems(response?.data);
	const paginationData: IPagination | undefined = normalizeSessionPagination(response?.data, currentPage, items.length);
	const summary = normalizeSessionsSummary(response?.data);
	const hasSummary = !!summary;
	const hasSummaryToShow = showSummary && !!(response?.data && (summary || !summaryFallbackDismissed));
	// Reset the "dismissed" flag whenever summary becomes available again — clearing
	// filters / narrowing date range brings the panel back fresh, even if user previously
	// dismissed it during a different filter combination.
	useEffect(() => {
		if (hasSummary) setSummaryFallbackDismissed(false);
	}, [hasSummary]);
	const collapsedTableColumnSet = new Set<string>(collapsedTableColumns);
	const noClientMerchantsError = isNoClientMerchantsError(response?.error);

	// Table-head pinning is driven by JS-transform on the header grid-cells. Pure CSS
	// sticky doesn't reliably keep grid items synced as a single visual row in this
	// configuration, so we lift each header cell with translateY in the scroll listener
	// below. The bundle above still uses real CSS `position: sticky` (one element,
	// no Grid quirk).
	const stickyHeadSx = {
		position: tableScrollable ? ("sticky" as const) : ("relative" as const),
		top: tableScrollable ? 0 : undefined,
		zIndex: 2,
		backgroundColor: "#e3dfd6",
	};
	// gridColumn rationale: the always-visible columns (id/status/type/amount/actions) use
	// `minmax(min, max-content)` rather than plain `max-content` so the grid can compress them
	// when the wrapper is narrower than the natural total width. Plain `max-content` is
	// inelastic — it forces the grid to claim at least sum-of-content widths, causing a
	// horizontal scrollbar to appear once everything collapsible has been hidden but the
	// remaining columns still don't fit. The `min` values are chosen to be the narrowest the
	// cell can render while remaining legible (long text inside ellipsizes; pill rows clip
	// from the right).
	const baseTableHeaders = [
		{ value: "id", gridColumn: "minmax(155px, max-content)" },
		{ value: "status", gridColumn: "minmax(155px, max-content)" },
		{ value: "type", gridColumn: "minmax(120px, max-content)" },
		...(shouldHideMerchantColumn ? [] : [{ value: "mid", gridColumn: "minmax(100px, 140px)" }]),
		{ value: "email", gridColumn: shouldHideMerchantColumn ? "minmax(200px, 1fr)" : "minmax(200px, 0.5fr)" },
		{ value: "instrument", gridColumn: "minmax(100px, 1fr)" },
		{ value: "fail_reason", gridColumn: "minmax(140px, 260px)" },
		{ value: "reserved", gridColumn: "max-content" },
		{ value: "fee", gridColumn: "max-content" },
		{ value: "amount", gridColumn: "minmax(100px, max-content)" },
		{ value: "actions", gridColumn: "max-content" },
	];
	// Priority of which remaining column should absorb leftover horizontal space when others
	// collapse. Instrument (Institution) leads: its CSS is already `minmax(100px, 1fr)`,
	// stretching is its native behavior. Email second (also a stretchy 1fr column). Mid
	// (Merchant) is the last-resort fallback: its CSS cap is normally `minmax(100px, 140px)`
	// but when chosen as stretch it absorbs the leftover via `minmax(140px, 1fr)` — only
	// happens when both instrument and email are already collapsed.
	// `id` is intentionally NOT a stretch candidate: it has fixed-width content (date stamp
	// + ID number), and turning it into a stretch column makes it visually balloon past its
	// natural footprint with no informational gain.
	const stretchColumn = collapsedTableColumns.length > 0 ? ["instrument", "email", "mid"].find(column => !collapsedTableColumnSet.has(column)) : undefined;
	const tableHeaders = baseTableHeaders
		.filter(header => !collapsedTableColumnSet.has(header.value))
		.map(header => ({
			...header,
			gridColumn: header.value === stretchColumn ? "minmax(140px, 1fr)" : header.gridColumn,
			sx: stickyHeadSx,
		}));
	const tableOrder = tableHeaders.map(header => header.value as any);
	const gridTemplateColumns = tableHeaders.map(header => header.gridColumn).join(" ");
	const headerCount = tableHeaders.length;
	const renderedColumnNames = tableHeaders.map(header => header.value).join("|");

	useEffect(() => {
		setCollapsedTableColumns([]);
	}, [target_id, target_type, shouldHideMerchantColumn]);

	useEffect(() => {
		const wrapper = scrollRef.current;
		if (!wrapper) return;

		let frameID: number | undefined;
		const getColumnWidth = (column: string) => sessionColumnWidthFallback[column] ?? 100;
		const getEstimatedTableWidth = (collapsedCount: number) => {
			const nextCollapsedColumns = new Set<string>(collapsibleSessionColumns.slice(0, collapsedCount));
			return baseTableHeaders.reduce((width, header) => {
				return nextCollapsedColumns.has(header.value) ? width : width + getColumnWidth(header.value);
			}, 0);
		};
		const measureColumns = () => {
			const grid = wrapper.firstElementChild as HTMLElement | null;
			if (!grid) return;

			const clientWidth = wrapper.clientWidth;
			if (clientWidth <= 0) return;

			let nextCollapsedCount = 0;
			while (
				nextCollapsedCount < collapsibleSessionColumns.length &&
				getEstimatedTableWidth(nextCollapsedCount) > clientWidth + TABLE_OVERFLOW_TOLERANCE
			) {
				nextCollapsedCount++;
			}

			const nextCollapsedColumns = collapsibleSessionColumns.slice(0, nextCollapsedCount);
			setCollapsedTableColumns(currentColumns => {
				if (currentColumns.length === nextCollapsedColumns.length && currentColumns.every((column, index) => column === nextCollapsedColumns[index]))
					return currentColumns;
				return nextCollapsedColumns;
			});
		};
		const scheduleMeasure = () => {
			if (frameID !== undefined) window.cancelAnimationFrame(frameID);
			frameID = window.requestAnimationFrame(measureColumns);
		};

		scheduleMeasure();
		const resizeObserver = new ResizeObserver(scheduleMeasure);
		resizeObserver.observe(wrapper);

		return () => {
			if (frameID !== undefined) window.cancelAnimationFrame(frameID);
			resizeObserver.disconnect();
		};
	}, [items, renderedColumnNames, collapsedTableColumns, shouldHideMerchantColumn]);

	// Track the height of the sticky top bundle (filter + summary) so the table-header
	// sticky logic below can use it as an offset. Without ResizeObserver, layout shifts
	// (filter wrapping on narrow viewport, summary appearing/disappearing) would leave
	// the table header overlapping the bundle or floating above the rows.
	useEffect(() => {
		if (tableScrollable) {
			setTopStickyHeight(0);
			return;
		}
		const el = topStickyRef.current;
		if (!el) return;

		const update = () => setTopStickyHeight(el.offsetHeight);
		update();
		const observer = new ResizeObserver(update);
		observer.observe(el);
		return () => observer.disconnect();
	}, [tableScrollable, filterable, summary]);

	// Table-head pinning via JS-transform. Each header grid-cell is lifted by translateY
	// to keep them visually stuck below the sticky bundle as the window scrolls.
	useEffect(() => {
		if (tableScrollable) return;

		const wrapper = scrollRef.current;
		if (!wrapper || items.length === 0) return;

		const grid = wrapper.firstElementChild as HTMLElement;
		if (!grid) return;

		const headerCells = Array.from(grid.children).slice(0, headerCount) as HTMLElement[];
		if (headerCells.length === 0) return;

		// +15 matches the outer VBox `gap="15px"` between the sticky bundle and the table
		// wrapper. Without it the table head sticks flush against the bundle, killing the
		// visual breathing room that exists when not scrolled.
		const STICKY_BUNDLE_TO_TABLE_GAP = 15;
		const stickyHeadTop = (headerHeight ?? 0) + topStickyHeight + (topStickyHeight > 0 ? STICKY_BUNDLE_TO_TABLE_GAP : 0);

		const handleScroll = () => {
			const wrapperRect = wrapper.getBoundingClientRect();
			const offset = stickyHeadTop - wrapperRect.top;
			const maxOffset = grid.offsetHeight - headerCells[0].offsetHeight;
			const clamped = Math.round(Math.max(0, Math.min(offset, maxOffset)));

			for (const cell of headerCells) {
				cell.style.transform = clamped > 0 ? `translateY(${clamped}px)` : "";
			}
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		handleScroll();

		return () => window.removeEventListener("scroll", handleScroll);
	}, [items, headerHeight, tableScrollable, renderedColumnNames, topStickyHeight, headerCount]);

	return (
		<VBox
			gap="15px"
			sx={{
				flexGrow: "1",
				width: "100%",
				height: tableScrollable ? "100%" : undefined,
				minHeight: tableScrollable ? 0 : undefined,
				// overflow:hidden creates a containing block for `position: sticky` — children
				// then can't escape it. Apply only when we genuinely need internal-scroll
				// (tableScrollable=true). When the page is the scroller (default), leave it
				// `visible` so the sticky command-bar can pin to the actual viewport top.
				overflow: tableScrollable ? "hidden" : undefined,
			}}>
			{csvExportJob && <CursorExportPopup job={csvExportJob} title={msg("exportTransactions")} description={msg("transactionsCsv")} onClose={() => setCsvExportJob(null)} />}
			{/*
			 * Sticky "command bar": filter + summary stay pinned to the top of the viewport so
			 * the user always sees the active filters and aggregate context while scrolling
			 * through rows. Only applies when the parent isn't already an internal scroller
			 * (tableScrollable=true means filter/summary are above the scroll region anyway,
			 * sticky inside an overflow:hidden ancestor doesn't behave as expected).
			 *
			 * The header bundle's offsetHeight is fed back into the table-head sticky logic
			 * (see useEffect above) so the table head pins immediately below this bundle —
			 * no overlap, no gap.
			 */}
			<VBox
				ref={topStickyRef}
				gap="15px"
				sx={{
					// Pure CSS sticky — relies on window being the scroll-ancestor.
					// Pages wrapping Sessions must NOT pass `separatePanelsScroll` to Page
					// (it puts overflow:auto on Section and steals sticky's scroll context).
					position: tableScrollable ? "relative" : ("sticky" as const),
					top: tableScrollable ? undefined : (headerHeight ?? 0) + "px",
					zIndex: 5,
					backgroundColor: surface,
					// 15px solid box-shadow extending below the bundle in the page-bg color
					// (#f8fafc). It visually covers the outer VBox gap between the sticky bundle
					// and the table — without it, rows scroll through that gap while the bundle
					// is pinned. Box-shadow doesn't affect layout, so the gap remains 15px.
					boxShadow: `0 15px 0 0 ${surface}`,
				}}>
			{filterable && filterCollapsed && (
				// Compact mode: only the search input + Search button. Filter-expand chip is
				// always shown; summary-expand chip joins this row only when summary is ALSO
				// collapsed (per spec: both collapsed → one row).
				<Box
					sx={{
						flexShrink: 0,
						background: "#fff",
						borderRadius: "14px",
						boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 2px 12px rgba(0,0,0,0.03)",
						...(filterAppearance === "portfolio" ? portfolioFilterSurface : {}),
					}}>
					<HBox gap={filterAppearance === "portfolio" ? "12px" : "10px"} sx={{ alignItems: "center", padding: filterAppearance === "portfolio" ? "12px 16px" : "10px 14px", flexWrap: "wrap" }}>
						<Input
							sx={{ flex: "1", minWidth: "190px" }}
							size="small"
							type="search"
							name="search"
							placeholder={msg("searching")}
							data={searchObject}
							label={msg("search")}
						/>
						<HBox gap="8px" sx={{ alignItems: "center", alignSelf: "end", height: "36px" }}>
							<Button onClick={() => handleSearch()} title={msg("search")} variant="primarySmall" />
							<ExpandPill label={msg("filters")} icon={<FilterIcon size={14} />} onClick={() => setFilterCollapsed(false)} />
							{hasSummaryToShow && summaryCollapsed && (
								<ExpandPill label={msg("summary")} icon={<BarChart3 size={14} />} onClick={() => setSummaryCollapsed(false)} />
							)}
						</HBox>
					</HBox>
				</Box>
			)}

			{filterable && !filterCollapsed && (
				<VBox
					sx={{
						flexShrink: 0,
						background: "#fff",
						borderRadius: "14px",
						boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 2px 12px rgba(0,0,0,0.03)",
						...(filterAppearance === "portfolio" ? portfolioFilterSurface : {}),
						padding: filterAppearance === "portfolio" ? "14px 16px" : "14px 20px",
						gap: filterAppearance === "portfolio" ? "12px" : "10px",
					}}>
					{/* Header row identical in shape to the Summary panel — same monospace
					    caps label on the left, same chevron-up collapse button on the right —
					    so the two panels feel like siblings. */}
					<HBox sx={{ justifyContent: "space-between", alignItems: "center", minHeight: "16px" }}>
						<Typo
							sx={{
								fontSize: "10px",
								color: "#94a3b8",
								textTransform: "uppercase",
								letterSpacing: "0.08em",
								fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
								fontWeight: 600,
								whiteSpace: "nowrap",
								...(filterAppearance === "portfolio" ? { fontSize: "12px", color: "#475467", textTransform: "none", letterSpacing: "normal", fontFamily: "inherit", fontWeight: 500 } : {}),
							}}>{msg("filter")}</Typo>
						<Box
							onClick={() => setFilterCollapsed(true)}
							title={msg("collapseFilter")}
							sx={{
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								width: "22px",
								height: "22px",
								borderRadius: "5px",
								color: "#94a3b8",
								cursor: "pointer",
								transition: "background-color 0.15s, color 0.15s",
								"&:hover": { backgroundColor: "#f1f5f9", color: "#475569" },
							}}>
							<ChevronUp size={14} />
						</Box>
					</HBox>
					<HBox gap={filterAppearance === "portfolio" ? "12px" : "8px"} sx={{ flexWrap: "wrap", alignItems: "start", flex: "1 1 auto" }}>
						<Input
							sx={{
								flex: "1",
								minWidth: "190px",
							}}
							size="small"
							type="search"
							name="search"
							placeholder={msg("searching")}
							data={searchObject}
							label={msg("search")}
						/>
						<DateRangePicker
							style={{ width: "290px", maxWidth: "100%" }}
							label={msg("dateRange")}
							timeZone="UTC"
							from={formatDateParam(fromDate)}
							to={formatDateParam(toDate)}
							onChange={(from, to) => {
								setFromDate(getValidDateOrFallback(from, defaultFromDate));
								setToDate(getValidDateOrFallback(to, defaultToDate));
							}}
						/>
						<Select<IFilterOption>
							sx={{ minWidth: "160px", maxWidth: "220px" }}
							size="small"
							label={msg("status")}
							value={statusFilter}
							values={statusOptions}
							onChange={value => setStatusFilter(getFilterOptionByValue(statusOptions, value as unknown as string))}
						/>
						<Select<IFilterOption>
							sx={{ minWidth: "160px", maxWidth: "220px" }}
							size="small"
							label={msg("type")}
							value={typeFilter}
							values={typeOptions}
							onChange={value => setTypeFilter(getFilterOptionByValue(typeOptions, value as unknown as string))}
						/>
						<Select<IFilterOption>
							sx={{ minWidth: "160px", maxWidth: "220px" }}
							size="small"
							label={msg("instrument")}
							value={instrumentFilter}
							values={instrumentOptions}
							onChange={value => setInstrumentFilter(getFilterOptionByValue(instrumentOptions, value as unknown as string))}
						/>
						<Select<IFilterOption>
							sx={{ minWidth: "160px", maxWidth: "220px" }}
							size="small"
							label={msg("settlementStatus")}
							value={settlementStatusFilter}
							values={settlementStatusOptions}
							onChange={value => setSettlementStatusFilter(getFilterOptionByValue(settlementStatusOptions, value as unknown as string))}
						/>
						<DatePickerField
							style={{ width: "190px", maxWidth: "100%" }}
							label={msg("settlementDate")}
							value={settlementDate}
							allowClear
							placeholder={msg("anyDate")}
							onChange={setSettlementDate}
						/>
						{canFilterByMerchant && <MerchantFilterControl merchant={merchantFilter} onClick={openMerchantFilter} />}

						<HBox gap="10px" sx={{ alignSelf: "end", height: "36px", marginLeft: "auto" }}>
							<Spacer grow />
							<Button onClick={() => resetSearch()} variant="secondarySmall" title={msg("reset")} />
							<Button onClick={() => handleSearch()} title={msg("search")} variant="primarySmall" />
						</HBox>
					</HBox>
				</VBox>
			)}

			<MerchantFilterPopup
				open={merchantFilterOpen}
				items={merchantFilterItems}
				busy={merchantFilterBusy}
				error={merchantFilterError}
				search={merchantSearch}
				selectedMerchant={merchantFilter}
				onSearchChange={value => setMerchantSearch({ search: value })}
				onSelect={selectMerchantFilter}
				onClose={() => setMerchantFilterOpen(false)}
			/>

			{hasSummaryToShow && !summaryCollapsed && (
				<SessionsSummaryPanels
					summary={summary}
					from={fromDate}
					to={toDate}
					onDismissFallback={() => setSummaryFallbackDismissed(true)}
					onCollapse={() => setSummaryCollapsed(true)}
				/>
			)}

			{/* Standalone summary-expand chip — when summary is collapsed but the filter is
			    NOT collapsed (or there is no filter). Spec says "both collapsed → merged row";
			    this covers the only-summary-collapsed case where the chip can't piggy-back on
			    the compact filter row. Right-aligned so it doesn't shout. */}
			{hasSummaryToShow && summaryCollapsed && !(filterable && filterCollapsed) && (
				<HBox sx={{ justifyContent: "flex-end" }}>
					<ExpandPill label={msg("summary")} icon={<BarChart3 size={14} />} onClick={() => setSummaryCollapsed(false)} />
				</HBox>
			)}
			</VBox>

			{noClientMerchantsError && <NoRecords line1={msg("ui_03d5bb8171")} line2={msg("ui_e1f81dd806")} />}
			{response?.error && !noClientMerchantsError && <Error error={response.error} />}

			<Box
				sx={{
					flex: tableScrollable ? "1 1 0" : "1 1 100%",
					minWidth: 0,
					minHeight: tableScrollable ? 0 : undefined,
					boxSizing: "border-box",
					overflow: tableScrollable ? "hidden" : undefined,
				}}>
				{response && response.data && (
					<div
						ref={scrollRef}
						style={{ overflowX: "auto", overflowY: tableScrollable ? "auto" : undefined, height: tableScrollable ? "100%" : undefined }}>
						<Table<IPaymentSession>
							headPadding="14px 12px"
							cellPadding="8px 12px"
							headSx={{ backgroundColor: "#e3dfd6", borderBottom: "none" }}
							sx={{ backgroundColor: surface, width: "100%" }}
							onRowClick={(row, _cell) => {
								if (onOpenSession) onOpenSession(row);
								else GD.S_TRANSACTION_PREVIEW.invoke(row);
							}}
							headerFactory={val => {
								if (val && val === "actions") return { content: <div style={{ width: "32px" }} /> };
								if (val && val === "mid") val = "Merchant";
								if (val && val === "email") val = "Customer";
								if (val && val === "instrument") val = "Institution";
								if (val && val === "fail_reason") val = "Fail reason";
								if (typeof val === "string") {
									return {
										content: (
											<Typo
												variant="medium"
												sx={{
													fontWeight: 400,
													fontSize: "11px",
													fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
													textTransform: "uppercase",
													letterSpacing: "0.06em",
													whiteSpace: "nowrap",
													color: "#000",
												}}>
												{val.toUpperCase()}
											</Typo>
										),
									};
								}
								return { content: null };
							}}
							headers={tableHeaders}
							order={tableOrder}
							gridTemplateColumns={gridTemplateColumns}
							appendCells={[{ header: <div />, value: "actions", gridColumn: "max-content" }]}
							rowSx={(row) => {
								// row-level tint for failed sessions — applied via Table's row-Box
								// wrapper which uses `display: contents`, so `& > div` targets every
								// cell. The `&:hover > div` overrides Table's default cellBackgroundHover
								// so the row keeps its red identity even when focused.
								if (row.status !== "failed") return undefined;
								return {
									"& > div": { backgroundColor: "rgba(239, 68, 68, 0.05)" },
									"&:hover > div": { backgroundColor: "rgba(239, 68, 68, 0.12)" },
								};
							}}
							cellFactory={(row, cell, _index, _key, name, _expanded, _rowID) => {
								if (name === "actions")
									return {
										content: <SessionActionMenu row={row} onForceWebhook={forceSessionWebhook} canChangeStatus={canChangeSessionStatus} />,
									};
								if (name === "status") return { content: <SessionStatusCell row={row} cell={cell} /> };
								if (name === "type") return { content: <SessionTypeCell row={row} /> };
								if (name === "mid") return { content: <SessionMerchantCell row={row} /> };
								if (name === "amount") return { content: <SessionAmountCell row={row} /> };
								if (name === "reserved") return { content: <SessionReservedCell row={row} /> };
								if (name === "fee") return { content: <SessionFeeCell row={row} /> };
								if (name === "email") return { content: <SessionEmailCell row={row} /> };
								if (name === "instrument") return { content: <SessionInstrumentCell row={row} /> };
								if (name === "fail_reason") return { content: <SessionFailReasonCell row={row} /> };
								if (name === "id") {
									// 4px left strip colored by row status — visual anchor for status at row level
									const sv = getStatusVisual(row.status);
									return { content: <SessionIdCell row={row} />, sx: { borderLeft: `4px solid ${sv.border}`, paddingLeft: "12px" } };
								}
								return { content: null };
							}}
							data={items}
						/>
					</div>
				)}
			</Box>

			<Box
				data-panel="footer"
				sx={{
					display: "flex",
					gap: "10px",
					alignItems: "center",
					flexWrap: "wrap",
					position: "sticky",
					bottom: 0,
					zIndex: 2,
					backgroundColor: surface,
					padding: "10px 0",
				}}>
				<Pagination pagination={paginationData} onPageChange={handlePageChange} disabled={_busy} />
				<Spacer grow />
				<HBox gap="10px" sx={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
					{user && project && (BATCH_EXPORT_USER_IDS as readonly number[]).includes(user.id) && (
						<Button title={msg("batchAnalysis")} icon={<Archive size={15} />} variant="secondarySmall" disabled={_busy || !!requestOptions?.signal?.aborted}
							onClick={() => {
								if (requestOptions?.expectedProjectId != null && requestOptions.expectedProjectId !== project.id) return;
								setBatchSnapshot({
									projectId: project.id, userId: user.id,
									filters: structuredClone(getRequest(0).data),
									expectedCount: paginationData?.total,
								});
							}} />
					)}
					<Button onClick={() => handleDownloadCsv()} title={csvExportJob ? "..." : msg("downloadCsv")} variant="secondarySmall" />
					<Button onClick={() => handleRefresh()} title={msg("refresh")} variant="primarySmall" />
				</HBox>
			</Box>
			{batchSnapshot && <TransactionBatchExportPopup snapshot={batchSnapshot} onClose={() => setBatchSnapshot(null)} />}
			{/*selectedSession && <SelectedSession session={selectedSession} onClose={() => setSelectedSession(null)} />*/}
		</VBox>
	);
};

Sessions.feature = "sessions";
